import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import type { PlaybookStatus } from '@doneverse/shared'
import { getNotifier } from '../api/index.js'
import {
  PlaybookNotReadyError,
  playbookCmsService,
  type PlaybookCmsPatch,
} from '../services/playbookCmsService.js'
import { cmsHistoryKeys } from './useCmsAssets.js'

/**
 * React Query bindings for the playbook CMS.
 *
 * Every mutation invalidates `playbookCms` wholesale rather than surgically:
 * the editor's tabs all read the same playbook, and a save on one tab can
 * change what another shows (publishing flips readiness, uploading a thumbnail
 * clears a blocking item). Precision here would buy nothing and cost
 * correctness.
 */

export const playbookCmsKeys = {
  all: ['playbookCms'] as const,
  list: (status?: PlaybookStatus) => ['playbookCms', 'list', status ?? 'all'] as const,
  detail: (identifier: string) => ['playbookCms', 'detail', identifier] as const,
  readiness: (id: string) => ['playbookCms', 'readiness', id] as const,
  videos: (playbookId: string) => ['playbookCms', 'videos', playbookId] as const,
}

export const playbookCmsListOptions = (status?: PlaybookStatus) =>
  queryOptions({
    queryKey: playbookCmsKeys.list(status),
    queryFn: () => playbookCmsService.list({ status }),
  })

export const playbookCmsDetailOptions = (identifier: string) =>
  queryOptions({
    queryKey: playbookCmsKeys.detail(identifier),
    queryFn: () => playbookCmsService.get(identifier),
    enabled: !!identifier,
  })

export const playbookCmsReadinessOptions = (id: string) =>
  queryOptions({
    queryKey: playbookCmsKeys.readiness(id),
    queryFn: () => playbookCmsService.readiness(id),
    enabled: !!id,
  })

export const playbookCmsVideosOptions = (playbookId: string) =>
  queryOptions({
    queryKey: playbookCmsKeys.videos(playbookId),
    queryFn: () => playbookCmsService.listVideos(playbookId),
    enabled: !!playbookId,
  })

function useInvalidateCms() {
  const queryClient = useQueryClient()
  // History is keyed outside `playbookCms` because it is shared with the other
  // CMS surfaces — but a playbook save is exactly when its history changes, so
  // it is invalidated alongside.
  return () => {
    queryClient.invalidateQueries({ queryKey: playbookCmsKeys.all })
    queryClient.invalidateQueries({ queryKey: cmsHistoryKeys.all })
  }
}

function message(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

export function useCreatePlaybook() {
  const invalidate = useInvalidateCms()
  return useMutation({
    mutationFn: (input: PlaybookCmsPatch & { name: string }) => playbookCmsService.create(input),
    onSuccess: () => invalidate(),
    onError: (error) => getNotifier().error(message(error, 'Failed to create playbook')),
  })
}

/**
 * Save authored fields.
 *
 * A refused publish is answered by the readiness checklist rather than a toast:
 * it names every missing field, where a toast names one. That only works if the
 * checklist is refreshed — the refusal means the server disagrees with the
 * readiness the page is currently showing, which is how the disabled Publish
 * button came to be clickable at all. Without the invalidate the click does
 * nothing visible. Every other failure toasts.
 */
export function useUpdatePlaybook() {
  const invalidate = useInvalidateCms()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PlaybookCmsPatch }) =>
      playbookCmsService.update(id, patch),
    onSuccess: () => invalidate(),
    onError: (error) => {
      if (error instanceof PlaybookNotReadyError) {
        invalidate()
        return
      }
      getNotifier().error(message(error, 'Failed to save playbook'))
    },
  })
}

export function useDeletePlaybook() {
  const invalidate = useInvalidateCms()
  return useMutation({
    mutationFn: (id: string) => playbookCmsService.remove(id),
    onSuccess: () => {
      invalidate()
      getNotifier().success('Playbook deleted')
    },
    onError: (error) => getNotifier().error(message(error, 'Failed to delete playbook')),
  })
}

export function useAddPlaybookVideo(playbookId: string) {
  const invalidate = useInvalidateCms()
  return useMutation({
    mutationFn: (input: Parameters<typeof playbookCmsService.addVideo>[1]) =>
      playbookCmsService.addVideo(playbookId, input),
    onSuccess: () => {
      invalidate()
      getNotifier().success('Video added')
    },
    onError: (error) => getNotifier().error(message(error, 'Failed to add video')),
  })
}

export function useRemovePlaybookVideo(playbookId: string) {
  const invalidate = useInvalidateCms()
  return useMutation({
    mutationFn: (videoId: string) => playbookCmsService.removeVideo(playbookId, videoId),
    onSuccess: () => {
      invalidate()
      getNotifier().success('Video removed')
    },
    onError: (error) => getNotifier().error(message(error, 'Failed to remove video')),
  })
}

export function useReorderPlaybookVideos(playbookId: string) {
  const invalidate = useInvalidateCms()
  return useMutation({
    mutationFn: (videoIds: string[]) => playbookCmsService.reorderVideos(playbookId, videoIds),
    onSuccess: () => invalidate(),
    onError: (error) => getNotifier().error(message(error, 'Failed to reorder videos')),
  })
}

export { PlaybookNotReadyError }
